import { create } from 'zustand';
import { apiClient, setStoredToken, getStoredToken } from './api';

export interface User {
  id: string;
  code: string;
  role: string;
  fullName?: string;
  fullNameEn?: string;
  fullNameAr?: string | null;
  fullNameUr?: string | null;
  phone: string;
  email?: string | null;
  preferredLanguage?: string;
  timezone?: string;
  status?: string;
  lastLoginAt?: string | null;
  mustChangePassword?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setSession: (token: string, user: User) => void;
  loadCurrentUser: () => Promise<void>;
  logout: () => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  token: getStoredToken(),
  isAuthenticated: !!getStoredToken(),
  isLoading: false,

  setSession: (token, user) => {
    setStoredToken(token);
    set({ token, user, isAuthenticated: true, isLoading: false });
  },

  loadCurrentUser: async () => {
    if (!get().token) return;
    set({ isLoading: true });
    try {
      const { data } = await apiClient.get<{ user: User }>('/api/v1/users/me');
      set({ user: data.user, isAuthenticated: true, isLoading: false });
    } catch {
      // 401 interceptor handles token cleanup
      set({ user: null, token: null, isAuthenticated: false, isLoading: false });
    }
  },

  logout: () => {
    setStoredToken(null);
    set({ user: null, token: null, isAuthenticated: false });
    window.location.href = '/login';
  },
}));

// PR-AUTH-HYDRATE MAY 11 — kick the /users/me fetch once at module load
// if a token is present. Previously `loadCurrentUser` only ran inside
// DashboardPage's useEffect, so direct nav (bookmark / refresh / new tab)
// to any other /dashboard/* page left `user` null and broke role-gated UI
// like the Top up button on /dashboard/water-suppliers. Not every route
// goes through ProtectedRoute (App.tsx wraps some routes individually but
// not all), so this needs to live at the store, not the guard.
// Fire-and-forget: loadCurrentUser handles its own errors and the 401
// interceptor in api.ts catches stale tokens.
if (typeof window !== 'undefined' && getStoredToken()) {
  void useAuth.getState().loadCurrentUser();
}
